// Phase P-LINK-VISA-NAT-TRIPLE
// Forward edges from /visa/$nationality (TH + EN twin) into the triple-key
// translation matrix /translate/english/$doctype/in/$location.
//
// Why: visa applicants of any type need the same core set of documents
// translated into English (passport, financial statements, bank book,
// transcript/diploma, marriage/birth certificates, police clearance,
// medical certificate). Deep-linking the nationality hub funnels PageRank
// from 40+ nationality pillars into long-tail money pages.

import { Link } from "@tanstack/react-router";
import { ArrowRight, Languages } from "lucide-react";
import { getTranslationDocType } from "@/data/translation-doctypes";

const VISA_CORE_DOCTYPES = [
  "passport",
  "financial-statement",
  "bank-statement",
  "marriage-certificate",
  "birth-certificate",
  "diploma",
  "transcript",
  "police-clearance",
  "medical-certificate",
] as const;

// Must stay subset of TRIPLE_KEY_LOCATIONS
const HUBS = [
  "bangkok-hq",
  "hub-sathorn-cbd",
  "hub-asoke-corridor",
  "hub-phuket-patong",
  "hub-chiangmai-nimman",
  "hub-pattaya",
] as const;

const HUB_LABEL_TH: Record<string, string> = {
  "bangkok-hq": "สำนักงานใหญ่ กรุงเทพฯ",
  "hub-asoke-corridor": "อโศก / สุขุมวิท",
  "hub-sathorn-cbd": "สาทร CBD",
  "hub-phuket-patong": "ภูเก็ต ป่าตอง",
  "hub-chiangmai-nimman": "เชียงใหม่ นิมมาน",
  "hub-pattaya": "พัทยา",
};
const HUB_LABEL_EN: Record<string, string> = {
  "bangkok-hq": "Bangkok HQ",
  "hub-asoke-corridor": "Asoke / Sukhumvit",
  "hub-sathorn-cbd": "Sathorn CBD",
  "hub-phuket-patong": "Phuket / Patong",
  "hub-chiangmai-nimman": "Chiang Mai / Nimman",
  "hub-pattaya": "Pattaya",
};

interface Props {
  mode: "th" | "en";
  nationalityNameTh: string;     // e.g. "ชาวอเมริกัน"
  nationalityDemonymEn: string;  // e.g. "American"
  countryEn: string;             // e.g. "United States"
}

export default function VisaNationalityTripleKeyLinks({
  mode, nationalityNameTh, nationalityDemonymEn, countryEn,
}: Props) {
  type Card = { slug: string; d: NonNullable<ReturnType<typeof getTranslationDocType>>; hub: string };
  const cards: Card[] = VISA_CORE_DOCTYPES
    .map((slug, i): Card | null => {
      const d = getTranslationDocType(slug);
      if (!d) return null;
      return { slug, d, hub: HUBS[i % HUBS.length] };
    })
    .filter((x): x is Card => x !== null)
    .slice(0, 6);

  if (cards.length === 0) return null;
  const isTh = mode === "th";

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-sky-700">
        {isTh ? `แปลเอกสารยื่นวีซ่าสำหรับ${nationalityNameTh}` : `Translate visa documents for ${nationalityDemonymEn} applicants`}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh
          ? `เอกสารยอดนิยมที่${nationalityNameTh}แปลก่อนยื่นวีซ่า`
          : `Most-translated documents for ${nationalityDemonymEn} visa applicants (${countryEn})`}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        {isTh
          ? "เลือกเอกสารและฮับที่ใกล้คุณ — รับรอง MoJ + ตรา MFA พร้อมยื่นสถานทูตและ ตม."
          : "Pick the document and hub nearest you — MoJ-certified translators + MFA seal, ready for embassy and Immigration submission."}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ slug, d, hub }) => (
          <Link
            key={`${slug}-${hub}`}
            to="/translate/$language/$doctype/in/$location"
            params={{ language: "english", doctype: slug, location: hub }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            data-track-zone="visa-nat-triple-deep"
          >
            <Languages className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">
                {isTh ? d.nameTh : d.nameEn}
              </div>
              <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-sky-700">
                {isTh ? `แปล ${d.nameTh} เป็นภาษาอังกฤษ` : `Translate ${d.nameEn} to English`}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {isTh ? `รับ/นัดที่ ${HUB_LABEL_TH[hub] ?? hub}` : `Pickup at ${HUB_LABEL_EN[hub] ?? hub}`}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 group-hover:gap-1.5">
                {isTh ? "ดูราคา + ขั้นตอน" : "View price + steps"} <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
